import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Star, Zap, Target, Users, Building2, ChevronDown, ChevronUp, Globe, CheckCircle2, Eye, Phone, Clock, MessageSquare, BookmarkPlus, Info, TrendingUp, Shield, AlertTriangle, Search, ArrowUpRight, Bookmark, Bot, Mail, Reply, Calendar, History } from 'lucide-react';
import { fetchWithAuth, API_URL } from './constants';
import { toast } from 'sonner';
import { QuickOutreachModal } from './QuickOutreachModal';
import { BatchOutreachModal } from './BatchOutreachModal';

/* ── Badge component ── */
const WhyBadge = ({ text, variant = 'default' }) => {
  const styles = {
    fit: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    activity: 'bg-blue-50 text-blue-700 border-blue-200',
    signal: 'bg-amber-50 text-amber-700 border-amber-200',
    override: 'bg-purple-50 text-purple-700 border-purple-200',
    default: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${styles[variant]}`} data-testid={`why-badge`}>
      {text}
    </span>
  );
};

const getWhyBadgeVariant = (text) => {
  if (/target|icp|mid-market|size/i.test(text)) return 'fit';
  if (/activity|hiring|active/i.test(text)) return 'activity';
  if (/override/i.test(text)) return 'override';
  if (/contact|growth/i.test(text)) return 'signal';
  return 'default';
};

/* ── Confidence indicator ── */
const ConfidenceBar = ({ level, indicators }) => {
  const colors = { high: 'bg-emerald-500', medium: 'bg-amber-400', low: 'bg-red-400' };
  const labels = { high: 'High', medium: 'Medium', low: 'Low' };
  const widths = { high: 'w-full', medium: 'w-2/3', low: 'w-1/3' };

  const indicatorLabels = {
    size_known: { label: 'Size Known', good: true },
    size_unknown: { label: 'Size Unknown', good: false },
    industry_known: { label: 'ICP Industry', good: true },
    industry_inferred: { label: 'Industry Inferred', good: false },
    industry_missing: { label: 'Industry Missing', good: false },
    enrichment_verified: { label: 'Enriched', good: true },
    enrichment_pending: { label: 'Not Enriched', good: false },
    manual_override_data: { label: 'Manual Data', good: true },
  };

  return (
    <div className="space-y-1" data-testid="confidence-bar">
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${colors[level]} ${widths[level]} rounded-full transition-all`} />
        </div>
        <span className="text-[10px] font-semibold text-gray-500">{labels[level]} confidence</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {indicators.map((ind, i) => {
          const info = indicatorLabels[ind];
          if (!info) return null;
          return (
            <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded ${info.good ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
              {info.good ? '\u2713' : '\u2022'} {info.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/* ── Status selector ── */
const STATUS_OPTIONS = [
  { value: 'none', label: 'Not Started', icon: null, color: 'text-gray-400 bg-gray-50 border-gray-200' },
  { value: 'reviewed', label: 'Reviewed', icon: Eye, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'contacted', label: 'Contacted', icon: Mail, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  { value: 'replied', label: 'Replied', icon: Reply, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { value: 'follow_up', label: 'Follow Up', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'meeting_booked', label: 'Meeting', icon: Calendar, color: 'text-violet-600 bg-violet-50 border-violet-200' },
  { value: 'favorited', label: 'Saved', icon: Bookmark, color: 'text-pink-600 bg-pink-50 border-pink-200' },
];

const StatusPill = ({ status, onUpdate, company, source }) => {
  const [open, setOpen] = useState(false);
  const current = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const Icon = current.icon;

  return (
    <div className="relative">
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border flex items-center gap-1 transition-colors ${current.color}`}
        data-testid={`status-pill-${company}`}>
        {Icon && <Icon className="h-3 w-3" />}
        {current.label}
        {source === 'autopilot' && <Bot className="h-2.5 w-2.5 opacity-60" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 w-36" data-testid="status-dropdown">
          {STATUS_OPTIONS.map(opt => (
            <button key={opt.value}
              onClick={(e) => { e.stopPropagation(); onUpdate(opt.value); setOpen(false); }}
              className={`w-full px-3 py-1.5 text-left text-[11px] flex items-center gap-2 hover:bg-gray-50 ${opt.value === status ? 'font-bold' : ''}`}>
              {opt.icon ? <opt.icon className="h-3 w-3" /> : <span className="w-3" />}
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Company Row ── */
const CompanyRow = ({ item, expanded, onToggle, onStatusUpdate, onNoteUpdate, onQuickOutreach, selected, onSelect }) => {
  const [noteText, setNoteText] = useState(item.work_note || '');
  const [savingNote, setSavingNote] = useState(false);

  const saveNote = async () => {
    setSavingNote(true);
    await onNoteUpdate(item.company, noteText);
    setSavingNote(false);
  };

  const tierColors = {
    high_fit: 'border-l-emerald-500',
    moderate_fit: 'border-l-gray-400',
  };

  return (
    <div className={`border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all border-l-4 ${tierColors[item.target_classification] || 'border-l-gray-300'}`}
      data-testid={`start-here-row-${item.rank}`}>
      <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={onToggle}>
        {/* Checkbox */}
        <input type="checkbox" checked={selected} onChange={(e) => { e.stopPropagation(); onSelect(item.company); }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37] shrink-0 cursor-pointer"
          data-testid={`select-${item.rank}`} />
        {/* Rank */}
        <div className="w-8 h-8 rounded-full bg-[#0A1F44] text-white flex items-center justify-center text-xs font-bold shrink-0" data-testid={`rank-${item.rank}`}>
          {item.rank}
        </div>

        {/* Company info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-[#0A1F44] truncate">{item.company}</span>
            <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${item.target_classification === 'high_fit' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
              {item.target_classification === 'high_fit' ? 'BEST IMMEDIATE' : 'BROADER QUALIFIED'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {item.why_company.slice(0, 3).map((w, i) => (
              <WhyBadge key={i} text={w} variant={getWhyBadgeVariant(w)} />
            ))}
            {item.why_company.length > 3 && (
              <span className="text-[10px] text-gray-400">+{item.why_company.length - 3}</span>
            )}
          </div>
        </div>

        {/* Metrics */}
        <div className="hidden sm:flex items-center gap-4 shrink-0">
          <div className="text-center">
            <p className="text-xs font-bold text-[#0A1F44]">{item.composite_score}</p>
            <p className="text-[9px] text-gray-400">Score</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-bold text-[#0A1F44]">{item.auto_score}</p>
            <p className="text-[9px] text-gray-400">Activity</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-bold text-[#0A1F44]">{item.employee_count ? item.employee_count.toLocaleString() : '—'}</p>
            <p className="text-[9px] text-gray-400">Emp</p>
          </div>
        </div>

        <StatusPill status={item.work_status} company={item.company} source={item.work_source} onUpdate={(s) => onStatusUpdate(item.company, s)} />

        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {/* Why now signals */}
          {item.why_now.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1"><Zap className="h-3 w-3 text-amber-500" /> Why Now</p>
              <div className="flex flex-wrap gap-1.5">
                {item.why_now.map((w, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-medium">{w}</span>
                ))}
              </div>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-[10px] text-gray-400 uppercase">Industry</p>
              <p className="text-xs font-semibold text-[#0A1F44]">{item.industry || 'Unknown'}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-[10px] text-gray-400 uppercase">Employees</p>
              <p className="text-xs font-semibold text-[#0A1F44]">{item.employee_count ? item.employee_count.toLocaleString() : 'Unknown'}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-[10px] text-gray-400 uppercase">7d / 30d Activity</p>
              <p className="text-xs font-semibold text-[#0A1F44]">{item.count_7d} / {item.count_30d}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-[10px] text-gray-400 uppercase">Departments</p>
              <p className="text-xs font-semibold text-[#0A1F44]">{item.departments?.length || 0}</p>
            </div>
          </div>

          {/* Top roles */}
          {item.top_roles?.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Top Roles</p>
              <div className="flex flex-wrap gap-1">
                {item.top_roles.map((r, i) => (
                  <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full">{r}</span>
                ))}
              </div>
            </div>
          )}

          {/* Confidence */}
          <ConfidenceBar level={item.confidence_level} indicators={item.confidence_indicators} />

          {/* All Why badges */}
          {item.why_company.length > 3 && (
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">All Signals</p>
              <div className="flex flex-wrap gap-1">
                {item.why_company.map((w, i) => (
                  <WhyBadge key={i} text={w} variant={getWhyBadgeVariant(w)} />
                ))}
              </div>
            </div>
          )}

          {/* Quick Outreach + Note */}
          <div className="flex items-start gap-3 pt-1">
            <button onClick={(e) => { e.stopPropagation(); onQuickOutreach(item); }}
              className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-[11px] font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1.5 shadow-sm shrink-0 transition-colors"
              data-testid={`quick-outreach-${item.rank}`}>
              <Mail className="h-3.5 w-3.5" /> Quick Outreach
            </button>
            <div className="flex-1">
              <div className="flex gap-2">
                <input value={noteText} onChange={e => setNoteText(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                  data-testid={`note-input-${item.rank}`}
                  onKeyDown={e => { if (e.key === 'Enter') saveNote(); }} />
                <button onClick={saveNote} disabled={savingNote}
                  className="px-3 py-1.5 text-[10px] font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50"
                  data-testid={`save-note-${item.rank}`}>
                  {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Summary Cards ── */
const SummaryBar = ({ summary }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="start-here-summary">
    <div className="p-3 bg-white border border-gray-200 rounded-xl">
      <p className="text-[10px] text-gray-400 uppercase">Top Targets</p>
      <p className="text-lg font-bold text-[#0A1F44]">{summary.top_count}</p>
    </div>
    <div className="p-3 bg-white border border-gray-200 rounded-xl">
      <p className="text-[10px] text-gray-400 uppercase">Best Immediate</p>
      <p className="text-lg font-bold text-emerald-600">{summary.high_fit}</p>
    </div>
    <div className="p-3 bg-white border border-gray-200 rounded-xl">
      <p className="text-[10px] text-gray-400 uppercase">Broader Qualified</p>
      <p className="text-lg font-bold text-gray-600">{summary.moderate_fit}</p>
    </div>
    <div className="p-3 bg-white border border-gray-200 rounded-xl">
      <p className="text-[10px] text-gray-400 uppercase">Avg Score</p>
      <p className="text-lg font-bold text-[#D4AF37]">{summary.avg_composite_score}</p>
    </div>
    <div className="p-3 bg-white border border-gray-200 rounded-xl">
      <p className="text-[10px] text-gray-400 uppercase">Worked</p>
      <p className="text-lg font-bold text-[#0A1F44]">{(summary.status_counts?.reviewed || 0) + (summary.status_counts?.contacted || 0) + (summary.status_counts?.follow_up || 0)}</p>
    </div>
  </div>
);

/* ── Auto-pilot History Panel ── */
const AutopilotHistoryPanel = ({ entries, loading }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="autopilot-history-panel">
    <div className="p-4 border-b border-gray-100 flex items-center gap-2">
      <Bot className="h-4 w-4 text-violet-500" />
      <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider">Auto-pilot Activity Log</h3>
    </div>
    <div className="max-h-64 overflow-y-auto">
      {loading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
      ) : entries.length === 0 ? (
        <div className="p-6 text-center text-xs text-gray-400">No auto-pilot activity yet</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {entries.map((e, i) => (
            <div key={i} className="px-4 py-2.5 hover:bg-gray-50/50" data-testid={`autopilot-entry-${i}`}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-[#0A1F44]">{e.company_name}</span>
                  {e.auto_applied ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-50 text-violet-600 border border-violet-200 rounded">APPLIED</span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-gray-100 text-gray-500 border border-gray-200 rounded">SKIPPED</span>
                  )}
                </div>
                <span className="text-[10px] text-gray-400">{new Date(e.at).toLocaleString()}</span>
              </div>
              <p className="text-[10px] text-gray-600">{e.reason}</p>
              {e.auto_applied && (
                <p className="text-[10px] text-gray-400 mt-0.5">{e.old_status} → {e.new_status}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ── Main Component ── */
export const StartHereTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [listSize, setListSize] = useState(25);
  const [filterStatus, setFilterStatus] = useState('all');
  const [autopilotEnabled, setAutopilotEnabled] = useState(false);
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [outreachTarget, setOutreachTarget] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);

  const loadAutopilotSettings = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/autopilot/settings`);
      setAutopilotEnabled(res.enabled);
    } catch { /* silent */ }
  }, []);

  const toggleAutopilot = async () => {
    setAutopilotLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/autopilot/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !autopilotEnabled }),
      });
      setAutopilotEnabled(res.enabled);
      toast.success(`Auto-pilot ${res.enabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to toggle auto-pilot'); }
    setAutopilotLoading(false);
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/autopilot/history?limit=30`);
      setHistoryEntries(res.entries || []);
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/start-here?limit=${listSize}`);
      setData(result);
    } catch { toast.error('Failed to load target list'); }
    setLoading(false);
  }, [listSize]);

  useEffect(() => { load(); loadAutopilotSettings(); }, [load, loadAutopilotSettings]);

  const handleStatusUpdate = async (company, status) => {
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/start-here/${encodeURIComponent(company)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setData(prev => ({
        ...prev,
        start_here: prev.start_here.map(c => c.company === company ? { ...c, work_status: status, work_source: 'manual' } : c),
      }));
      toast.success(`${company}: ${status.replace('_', ' ')}`);
    } catch { toast.error('Failed to update status'); }
  };

  const handleNoteUpdate = async (company, note) => {
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/start-here/${encodeURIComponent(company)}/note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      setData(prev => ({
        ...prev,
        start_here: prev.start_here.map(c => c.company === company ? { ...c, work_note: note } : c),
      }));
      toast.success('Note saved');
    } catch { toast.error('Failed to save note'); }
  };

  const handleOutreachSent = (companyName) => {
    setData(prev => ({
      ...prev,
      start_here: prev.start_here.map(c =>
        c.company === companyName ? { ...c, work_status: 'contacted', work_source: 'autopilot' } : c
      ),
    }));
  };

  const toggleSelect = (name) => {
    setSelectedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectTopN = (n) => {
    const SKIP = new Set(['replied', 'meeting_booked', 'follow_up']);
    const safe = filtered.filter(c => !SKIP.has(c.work_status)).slice(0, n);
    setSelectedCompanies(new Set(safe.map(c => c.company)));
  };

  const clearSelection = () => setSelectedCompanies(new Set());

  const handleBatchComplete = () => {
    setSelectedCompanies(new Set());
    load();
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!data) return null;

  const items = data.start_here || [];
  const filtered = items.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.company.toLowerCase().includes(q) && !(c.industry || '').toLowerCase().includes(q)) return false;
    }
    if (filterStatus !== 'all' && c.work_status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="start-here-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#0A1F44] flex items-center gap-2">
            <Target className="h-5 w-5 text-[#D4AF37]" /> Start Here
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Your best-ranked accounts to work first, based on ICP fit, hiring activity, and data confidence</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-pilot toggle */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${autopilotEnabled ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-200'}`}>
            <Bot className={`h-3.5 w-3.5 ${autopilotEnabled ? 'text-violet-600' : 'text-gray-400'}`} />
            <span className="text-[10px] font-semibold text-gray-600">Auto-pilot</span>
            <button onClick={toggleAutopilot} disabled={autopilotLoading}
              className={`relative w-8 h-4 rounded-full transition-colors ${autopilotEnabled ? 'bg-violet-500' : 'bg-gray-300'}`}
              data-testid="autopilot-toggle">
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${autopilotEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {/* History button */}
          <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
            className={`px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border flex items-center gap-1 transition-colors ${showHistory ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
            data-testid="autopilot-history-btn">
            <History className="h-3 w-3" /> Log
          </button>
          <select value={listSize} onChange={e => setListSize(Number(e.target.value))}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#D4AF37]/30"
            data-testid="list-size-selector">
            <option value={25}>Top 25</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>
          <p className="text-[10px] text-gray-400">
            + {data.broader_pool_count} in broader pool
          </p>
        </div>
      </div>

      <SummaryBar summary={data.summary} />

      {/* Auto-pilot History */}
      {showHistory && <AutopilotHistoryPanel entries={historyEntries} loading={historyLoading} />}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search companies..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
            data-testid="start-here-search" />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {['all', 'none', 'reviewed', 'contacted', 'replied', 'meeting_booked', 'follow_up', 'favorited'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-2.5 py-1.5 text-[10px] font-semibold rounded-full border transition-colors ${filterStatus === s ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
              data-testid={`filter-${s}`}>
              {s === 'all' ? 'All' : s === 'none' ? 'Unworked' : s === 'meeting_booked' ? 'Meeting' : s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Selection Bar */}
      {selectedCompanies.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-[#0A1F44]/5 border border-[#D4AF37]/30 rounded-xl" data-testid="selection-bar">
          <span className="text-xs font-bold text-[#0A1F44]">{selectedCompanies.size} selected</span>
          <button onClick={clearSelection} className="text-[10px] text-gray-500 hover:text-gray-700 underline">Clear</button>
          <div className="flex-1" />
          <button onClick={() => setShowBatchModal(true)}
            className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1.5 shadow-sm"
            data-testid="batch-outreach-btn">
            <Mail className="h-3.5 w-3.5" /> Batch Outreach
          </button>
        </div>
      )}

      {/* Quick Select */}
      {selectedCompanies.size === 0 && filtered.length > 0 && (
        <div className="flex items-center gap-2" data-testid="quick-select-bar">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Quick select:</span>
          <button onClick={() => selectTopN(5)} className="px-2.5 py-1 text-[10px] font-semibold text-[#0A1F44] bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="select-top-5">Top 5</button>
          <button onClick={() => selectTopN(10)} className="px-2.5 py-1 text-[10px] font-semibold text-[#0A1F44] bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="select-top-10">Top 10</button>
          <button onClick={() => selectTopN(25)} className="px-2.5 py-1 text-[10px] font-semibold text-[#0A1F44] bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="select-top-25">Top 25</button>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
            <Target className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No companies match your filters</p>
          </div>
        ) : (
          filtered.map(item => (
            <CompanyRow key={item.company} item={item}
              expanded={expanded === item.company}
              onToggle={() => setExpanded(expanded === item.company ? null : item.company)}
              onStatusUpdate={handleStatusUpdate}
              onNoteUpdate={handleNoteUpdate}
              onQuickOutreach={(comp) => setOutreachTarget(comp)}
              selected={selectedCompanies.has(item.company)}
              onSelect={toggleSelect} />
          ))
        )}
      </div>

      {/* Broader pool info */}
      {data.broader_pool_count > 0 && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex items-center gap-2" data-testid="broader-pool-info">
          <Info className="h-4 w-4 text-gray-400 shrink-0" />
          <p className="text-xs text-gray-500">
            <strong>{data.broader_pool_count}</strong> more qualified companies in the broader pool.
            Switch to the <strong>Pipeline</strong> tab to see the full universe.
          </p>
        </div>
      )}

      {/* Quick Outreach Modal */}
      {outreachTarget && (
        <QuickOutreachModal
          company={outreachTarget}
          onClose={() => setOutreachTarget(null)}
          onSent={handleOutreachSent} />
      )}

      {/* Batch Outreach Modal */}
      {showBatchModal && (
        <BatchOutreachModal
          selectedCompanies={Array.from(selectedCompanies)}
          onClose={() => setShowBatchModal(false)}
          onComplete={handleBatchComplete} />
      )}
    </div>
  );
};
